import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useAuthInfo } from '@propelauth/react';

export enum FetchMethod {
  GET = 'GET',
  PATCH = 'PATCH',
  POST = 'POST',
  DELETE = 'DELETE',
  PUT = 'PUT',
}

interface UseGetFetchConfig {
  method: FetchMethod;
  data?: Record<string, any>;
  isFormData?: boolean;
}

export interface FetchConfig {
  method: FetchMethod;
  mode: RequestMode;
  cache: RequestCache;
  credentials: RequestCredentials;
  headers: Headers;
}

export const useFetcher = <T>() => {
  const authInfo = useAuthInfo();
  const authInfoRef = useRef(authInfo);

  useEffect(() => {
    authInfoRef.current = authInfo;
  }, [authInfo]);

  const fetcher = async (url: string): Promise<T> => {
    const res = await fetch(url, {
      method: FetchMethod.GET,
      mode: 'cors' as RequestMode,
      cache: 'no-cache' as RequestCache,
      credentials: 'same-origin' as RequestCredentials,
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authInfoRef.current.accessToken}`,
      }),
    });

    if (!res.ok) {
      throw new Error(`${res.status}: ${res.statusText}`);
    }

    return res.json() as T;
  };

  return fetcher;
};

const useGetFetchConfig = () => {
  // TODO: Deprecate
  const authInfo = useAuthInfo();
  const accessToken = authInfo.accessToken;

  const fetchConfigGET: FetchConfig = useMemo(
    () => ({
      method: FetchMethod.GET,
      mode: 'cors' as RequestMode,
      cache: 'no-cache' as RequestCache,
      credentials: 'same-origin' as RequestCredentials,
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      }),
    }),
    [accessToken],
  );

  const getFetchConfig = useCallback(
    ({ method, data, isFormData }: UseGetFetchConfig) => {
      const body = isFormData ? (data as FormData) : JSON.stringify(data);

      const headers: HeadersInit = isFormData
        ? {
            Authorization: `Bearer ${accessToken}`,
          }
        : {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          };

      return {
        method,
        cache: 'no-cache' as RequestCache,
        credentials: 'same-origin' as RequestCredentials,
        headers: new Headers(headers),
        body: data ? body : undefined,
      };
    },
    [accessToken],
  );

  return {
    fetchConfigGET,
    getFetchConfig,
  };
};

export default useGetFetchConfig;
